import React, { useState } from 'react';
import {
  Container, Card, CardContent, Typography, Button, Box,
  CircularProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, Tab, IconButton, FormControl,
  InputLabel, Select, MenuItem, Paper, Chip
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GradingIcon from '@mui/icons-material/Grading';
import PrintIcon from '@mui/icons-material/Print';
import * as XLSX from 'xlsx';
import { API_URL } from '../../config';
import WhatsAppSendButton from '../common/WhatsAppSendButton';

const GREEN  = '#1b4332';
const ORANGE = '#f59e0b';

// ─── Report table column header style ────────────────────────────────────────
const HDR_SX = {
  fontWeight: 700,
  color: '#fff',
  fontSize: '0.72rem',
  textTransform: 'uppercase',
  background: `linear-gradient(135deg, ${GREEN} 0%, #2d6a4f 100%)`,
  borderRight: '1px solid rgba(255,255,255,0.2)',
  py: 1.2,
  px: 1,
  whiteSpace: 'nowrap',
  textAlign: 'center',
};

const CELL_SX = { py: 0.8, px: 1, fontSize: '0.8rem', textAlign: 'center', whiteSpace: 'nowrap' };

function fmtNum(v, d = 3) {
  if (v === null || v === undefined || v === '') return '—';
  const n = parseFloat(v);
  return isNaN(n) ? '—' : n.toFixed(d);
}

// ─── Shift chip ───────────────────────────────────────────────────────────────
function ShiftChip({ shift }) {
  const s = String(shift || '').toUpperCase();
  return (
    <Chip
      label={shift || '-'}
      size="small"
      sx={{
        fontWeight: 700,
        fontSize: '0.7rem',
        backgroundColor: s === 'DAY' ? '#fef3c7' : s === 'NIGHT' ? '#dbeafe' : '#f3f4f6',
        color: s === 'DAY' ? '#92400e' : s === 'NIGHT' ? '#1e40af' : '#374151',
      }}
    />
  );
}

// ─── Pallet Detail Table ──────────────────────────────────────────────────────
function PalletTable({ rows, shift }) {
  if (!rows || rows.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No data found</Alert>;

  // If BOTH — group by shift with divider rows
  const showShiftCol = shift === 'BOTH';

  const cols = [
    { label: 'S.NO',       key: 'SNO',          fmt: null },
    ...(showShiftCol ? [{ label: 'SHIFT', key: 'SHIFT', isShift: true }] : []),
    { label: 'PALLET NO',  key: 'PALLET_NO',    fmt: null },
    { label: 'THICKNESS',  key: 'THICKNESS',    fmt: v => fmtNum(v, 2) },
    { label: 'SIZE',       key: 'SIZE_DISPLAY', fmt: null },
    { label: 'NO. OF PCS', key: 'PCS',          fmt: v => fmtNum(v, 0) },
    { label: 'LOT NO.',    key: 'LOT_NO',       fmt: null },
    { label: 'QUALITY',    key: 'QUALITY',      fmt: null },
    { label: 'CBM',        key: 'CBM',          fmt: v => fmtNum(v, 3) },
  ];

  return (
    <TableContainer sx={{ maxHeight: 560, overflowX: 'auto' }}>
      <Table stickyHeader size="small" sx={{ minWidth: 750 }}>
        <TableHead>
          <TableRow>
            {cols.map(c => <TableCell key={c.label} sx={HDR_SX}>{c.label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i} sx={{
              backgroundColor: i % 2 === 0 ? '#fff' : '#f0fdf4',
              '&:hover': { backgroundColor: '#d1fae5' },
            }}>
              {cols.map(col => {
                if (col.isShift) {
                  return (
                    <TableCell key={col.label} sx={CELL_SX}>
                      <ShiftChip shift={row[col.key]} />
                    </TableCell>
                  );
                }
                const val = col.fmt ? col.fmt(row[col.key]) : (row[col.key] ?? '—');
                return <TableCell key={col.label} sx={CELL_SX}>{val}</TableCell>;
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Summary Table ────────────────────────────────────────────────────────────
function SummaryTable({ rows, title, color = GREEN }) {
  if (!rows || rows.length === 0) return null;
  return (
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color, mb: 0.5, px: 1 }}>
        {title}
      </Typography>
      <Table size="small" sx={{ minWidth: 400 }}>
        <TableHead>
          <TableRow>
            {['SHIFT', 'PALLETS', 'PCS', 'CBM'].map(h => (
              <TableCell key={h} sx={{ ...HDR_SX, background: `linear-gradient(135deg, ${color} 0%, #374151 100%)` }}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => {
            const isOverall = String(row.SHIFT || '').toUpperCase() === 'OVERALL';
            return (
              <TableRow key={i} sx={{
                backgroundColor: isOverall ? '#1b4332' : (i % 2 === 0 ? '#fff' : '#f0fdf4'),
              }}>
                <TableCell sx={{ ...CELL_SX, fontWeight: isOverall ? 700 : 600, color: isOverall ? '#fff' : 'inherit' }}>
                  {isOverall ? 'OVERALL' : <ShiftChip shift={row.SHIFT} />}
                </TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: isOverall ? 700 : 500, color: isOverall ? '#a7f3d0' : 'inherit' }}>
                  {fmtNum(row.TOTAL_PALLETS, 0)}
                </TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: isOverall ? 700 : 500, color: isOverall ? '#a7f3d0' : GREEN }}>
                  {fmtNum(row.TOTAL_PCS, 0)}
                </TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: isOverall ? 700 : 500, color: isOverall ? '#a7f3d0' : GREEN }}>
                  {fmtNum(row.TOTAL_CBM, 3)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

// ─── Stat chip ────────────────────────────────────────────────────────────────
function StatChip({ label, value, color = GREEN }) {
  return (
    <Paper elevation={0} sx={{
      px: 2, py: 1.5, borderRadius: 2, textAlign: 'center', minWidth: 110,
      border: `1.5px solid ${color}30`, background: `${color}08`,
    }}>
      <Typography sx={{ fontSize: '0.68rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color }}>{value}</Typography>
    </Paper>
  );
}

// ─── Main View ────────────────────────────────────────────────────────────────
const TABS = ['DETAIL', 'DAILY SUMMARY', 'MONTH TOTAL'];

const GradingPalletReportView = ({ onBackClick, user }) => {
  const [date,    setDate]    = useState(null);
  const [shift,   setShift]   = useState('DAY');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [data,    setData]    = useState(null);
  const [tab,     setTab]     = useState(0);

  const fetchData = async () => {
    if (!date) { setError('Please select a date'); return; }
    setLoading(true); setError(''); setData(null);
    try {
      const res = await fetch(`${API_URL}/order/gradingpalletreport`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: date.format('YYYY-MM-DD'), shift }),
      });
      if (!res.ok) throw new Error('API error: ' + res.statusText);
      const apiData = await res.json();
      if (!apiData.data || apiData.data[0]?.length === 0) {
        setError('No data found for the selected date and shift.');
        return;
      }
      setData(apiData.data);
      setTab(0);
    } catch (e) {
      setError(e.message || 'Fetch failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    if (data[0]?.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data[0]), 'Detail');
    if (data[1]?.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data[1]), 'Daily Summary');
    if (data[2]?.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data[2]), 'Month Total');
    XLSX.writeFile(wb, `GradingPalletReport_${date.format('YYYY-MM-DD')}_${shift}.xlsx`);
  };

  // Quick stats from daily summary
  const overall = data?.[1]?.find(r => String(r.SHIFT).toUpperCase() === 'OVERALL')
               ?? data?.[1]?.[0];

  // Month total overall row
  const mtdOverall = data?.[2]?.find(r => String(r.SHIFT).toUpperCase() === 'OVERALL')
                  ?? data?.[2]?.[0];

  return (
    <Container maxWidth="xl" sx={{ mt: 2, pb: 4, px: { xs: 1, md: 2 } }}>
      {/* Header card */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 12px rgba(27,67,50,0.1)', border: '1px solid #d1fae5' }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={3}>
            {onBackClick && (
              <IconButton onClick={onBackClick} sx={{ mr: 2, color: GREEN }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box sx={{ background: `linear-gradient(135deg, ${GREEN} 0%, #2d6a4f 100%)`, borderRadius: 2, p: 1.5, mr: 2 }}>
              <GradingIcon sx={{ fontSize: 28, color: '#fff' }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: GREEN }}>Grading Pallet Report</Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>Plywood grading by date and shift</Typography>
            </Box>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} alignItems="center">
              <DatePicker
                label="Date"
                value={date}
                onChange={setDate}
                slotProps={{ textField: { fullWidth: true, sx: { flex: 1 } } }}
              />
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Shift</InputLabel>
                <Select value={shift} label="Shift" onChange={e => setShift(e.target.value)}>
                  <MenuItem value="DAY">DAY</MenuItem>
                  <MenuItem value="NIGHT">NIGHT</MenuItem>
                  <MenuItem value="BOTH">BOTH (DAY + NIGHT)</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained" size="large" startIcon={<SearchIcon />}
                onClick={fetchData} disabled={loading}
                sx={{
                  minWidth: 140, backgroundColor: GREEN,
                  '&:hover': { backgroundColor: '#0f2419' },
                  borderRadius: 2, textTransform: 'none', fontWeight: 600,
                }}
              >
                Search
              </Button>
            </Box>
          </LocalizationProvider>

          {/* Quick stats */}
          {overall && (
            <Box display="flex" flexWrap="wrap" gap={1.5} mt={2}>
              <StatChip label="Today Pallets" value={fmtNum(overall.TOTAL_PALLETS, 0)} />
              <StatChip label="Today PCS"     value={fmtNum(overall.TOTAL_PCS, 0)}     color={GREEN} />
              <StatChip label="Today CBM"     value={fmtNum(overall.TOTAL_CBM, 3)}     color={GREEN} />
              {mtdOverall && <>
                <StatChip label="Month Pallets" value={fmtNum(mtdOverall.TOTAL_PALLETS, 0)} color={ORANGE} />
                <StatChip label="Month PCS"     value={fmtNum(mtdOverall.TOTAL_PCS, 0)}     color={ORANGE} />
                <StatChip label="Month CBM"     value={fmtNum(mtdOverall.TOTAL_CBM, 3)}     color={ORANGE} />
              </>}
            </Box>
          )}
        </CardContent>
      </Card>

      {loading && <Box textAlign="center" py={6}><CircularProgress sx={{ color: GREEN }} /></Box>}
      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #d1fae5' }}>
          <Box sx={{
            borderBottom: 1, borderColor: 'divider',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            px: 2, pt: 1,
          }}>
            <Tabs
              value={tab} onChange={(_, v) => setTab(v)}
              textColor="inherit" variant="scrollable" scrollButtons="auto"
              sx={{
                '& .MuiTab-root': { fontWeight: 600, fontSize: '0.78rem' },
                '& .Mui-selected': { color: GREEN },
                '& .MuiTabs-indicator': { backgroundColor: GREEN },
              }}
            >
              {TABS.map((t, i) => (
                <Tab key={i} label={i === 0 ? `${t} (${data[0]?.length || 0})` : t} />
              ))}
            </Tabs>
            <Box display="flex" gap={1}>
              <Button
                size="small" startIcon={<DownloadIcon />} onClick={handleDownload}
                sx={{ color: GREEN, fontWeight: 600, textTransform: 'none', flexShrink: 0 }}
              >
                Excel
              </Button>
            </Box>
          </Box>

          {/* DETAIL TAB */}
          {tab === 0 && <PalletTable rows={data[0]} shift={shift} />}

          {/* DAILY SUMMARY TAB */}
          {tab === 1 && (
            <Box sx={{ p: 2 }}>
              <SummaryTable
                rows={data[1]}
                title={`Daily Summary — ${date?.format('DD MMM YYYY')}`}
                color={GREEN}
              />
            </Box>
          )}

          {/* MONTH TOTAL TAB */}
          {tab === 2 && (
            <Box sx={{ p: 2 }}>
              {data[2]?.[0] && (
                <Typography sx={{ fontSize: '0.75rem', color: '#666', mb: 1 }}>
                  Month-to-date: {data[2][0].MTD_FROM?.substring(0, 10)} to {data[2][0].MTD_TO?.substring(0, 10)}
                </Typography>
              )}
              <SummaryTable
                rows={data[2]}
                title={`Month Total — ${date?.format('MMMM YYYY')}`}
                color={ORANGE}
              />
            </Box>
          )}
        </Card>
      )}

      {/* WhatsApp Send */}
      {data && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
          <WhatsAppSendButton
            user={user}
            label={`Send Grading Pallet Report to WhatsApp`}
            apiUrl={
              date
                ? `${API_URL}/api/gradingpallet/send?date=${date.format('YYYY-MM-DD')}&shift=${shift}`
                : null
            }
            disabled={!date}
          />
        </Box>
      )}
    </Container>
  );
};

export default GradingPalletReportView;
